import { select, call, takeLatest, put } from 'redux-saga/effects';

import { VendorActions } from '../constants';
import { VendorApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import {
    DeleteVendorAction,
    GetVendorAction,
    GetVendorsAction,
    PersistVendorAction,
    CreateFilesVendorAction,
    GetFilesAction,
    DeleteFileVendorAction,
} from '../actions/vendor-actions';
import sortingService from '../../services/sorting';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { convertValuesToFormData } from '../../components/redux-form/utils/values-to-form-data';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { changeToZeroMomentDate } from '../../helpers/timeHelpers';

function* handleFetchVendors(action: GetVendorsAction) {
    const oldData = yield select((state: StoreState) => state.vendors.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    let filterProps: AnyObject = { ...filters };
    if (filters?.createdAt) {
        filterProps = {
            ...filters,
            'createdAt[before]': changeToZeroMomentDate(`${filters.createdAt}`),
            'createdAt[after]': changeToZeroMomentDate(`${filters.createdAt}`),
        };
        delete filterProps.createdAt;
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        VendorApi.getVendors({
            ...filterProps,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}
function* handleFetchVendorsSmall(action: GetVendorsAction) {
    const oldData = yield select((state: StoreState) => state.vendors.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;

    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        VendorApi.getVendorsSmall({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchVendor(action: GetVendorAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => VendorApi.getVendor(id));
}

function* handleDeleteVendor(action: DeleteVendorAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => VendorApi.deleteVendor(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistVendor(action: PersistVendorAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        files,
        callbacks,
    } = payload;

    yield resolveApiCall(
        action,
        () => (id ? VendorApi.updateVendor(id, restData) : VendorApi.createVendor(restData)),
        function* (data: ResolverApiSuccess) {
            const actionFiles: CreateFilesVendorAction = {
                type: VendorActions.CREATE_FILES_VENDOR,
                payload: {
                    id: data.payload.id,
                    files,
                    callbacks,
                },
            };
            yield call(handleCreateFilesVendor, actionFiles);
            yield call(callbacks.resolve);
            if (!id) {
                yield put(push(Router.generate(routes.VENDORS_LIST)));
            } else {
                yield put(push(Router.generate(routes.VENDOR_DETAIL, { id })));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            yield handleError(result);
        },
    );
}

function* handleGetFilesVendor(action: GetFilesAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => VendorApi.getFilesVendor(id));
}

function* handleCreateFilesVendor(action: CreateFilesVendorAction) {
    const {
        payload: { id, files, callbacks },
    } = action;

    for (let i = 0; i < files.length; i++) {
        if (files[i].id || files[i]?.attach) {
            continue;
        }
        const formData = convertValuesToFormData({ vendor: id, attachFile: files[i] }, '');
        yield resolveApiCall(
            action,
            () => VendorApi.createFilesVendor(formData),
            function* (data: ResolverApiSuccess) {
                yield call(callbacks.resolve);
            },
            function* (result: ResolverApiFailure) {
                if (result.error && result.error.response && result.error.response.status) {
                    yield call(
                        callbacks.reject,
                        result.error instanceof SubmissionError
                            ? result.error
                            : new SubmissionError({
                                  _error: result.error.message ?? 'Incorrect response',
                                  ...makeFormErrors(result.error.response?.data),
                              }),
                    );
                }
                yield handleError(result);
            },
        );
    }
}

function* handleDeleteFileVendor(action: DeleteFileVendorAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => VendorApi.deleteFileVendor(id),
        function (data) {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.response.data.detail ?? result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(VendorActions.FETCH_VENDORS, handleFetchVendors);
    yield takeLatest(VendorActions.FETCH_VENDORS_SIMPLY, handleFetchVendorsSmall);
    yield takeLatest(VendorActions.FETCH_VENDOR, handleFetchVendor);
    yield takeLatest(VendorActions.DELETE_VENDOR, handleDeleteVendor);
    yield takeLatest(VendorActions.PERSIST_VENDOR, handlePersistVendor);
    yield takeLatest(VendorActions.DELETE_FILE_VENDOR, handleDeleteFileVendor);
    yield takeLatest(VendorActions.CREATE_FILES_VENDOR, handleCreateFilesVendor);
    yield takeLatest(VendorActions.FETCH_FILES_VENDOR, handleGetFilesVendor);
}
